/**
 * `/casos/:id/conversa` (E149.F2, GUS-149, ADR-0102 §3): a conversa do caso com o cliente, na área
 * da equipe. O que aguarda a equipe (questionamento e apontamento) se responde na própria
 * mensagem; o pedido de documento, a mensagem e a ciência do cliente se registram no fim. O link
 * sai da esteira do caso, ao lado do histórico.
 */
import { Link, useParams } from 'react-router-dom';

import { EscreverNaEquipe } from '@/components/ConversaDoCaso/EscreverNaEquipe';
import { ListaDeMensagens } from '@/components/ConversaDoCaso/ListaDeMensagens';
import { acaoDaEquipe, PendentesDaEquipe } from '@/components/ConversaDoCaso/PendentesDaEquipe';
import { useConversaDoCaso } from '@/components/ConversaDoCaso/useConversaDoCaso';
import { ProximoPasso } from '@/components/ProximoPasso/ProximoPasso';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';

const t = ptBR.conversaDoCaso;

export function ConversaDoCasoPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const { conversa, erro, recarregar, enviar } = useConversaDoCaso(caseId);

  return (
    <section
      className="mx-auto flex max-w-4xl flex-col gap-sc-6 p-sc-6"
      data-testid="conversa-page"
    >
      <header>
        <h1 className="text-display-sm text-fg">{t.titulo}</h1>
        <p className="mt-1 text-body-sm text-fg-muted">{t.equipe.subtitulo}</p>
        <Link className="mt-sc-2 inline-block text-body-sm text-accent-700" to={`/casos/${caseId}`}>
          {t.equipe.voltar}
        </Link>
      </header>
      <ProximoPasso client={client} caseId={caseId} />
      {erro !== null ? (
        <div className="space-y-2">
          <p role="alert" className="text-body-sm text-danger" data-testid="conversa-erro">
            {erro === 'recusado' ? t.equipe.semPermissao : t.erroCarregar}
          </p>
          {erro === 'falha' ? (
            <Button type="button" variant="secondary" onClick={() => void recarregar()}>
              {t.tentarDeNovo}
            </Button>
          ) : null}
        </div>
      ) : conversa === null ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : (
        <>
          <PendentesDaEquipe pendencias={conversa.pendencias} />
          <ListaDeMensagens mensagens={conversa.mensagens} acaoDoItem={acaoDaEquipe(enviar)} />
          <EscreverNaEquipe entregue={conversa.entregue} enviar={enviar} />
        </>
      )}
    </section>
  );
}
